import React, { useState } from "react";
import { FaFileInvoiceDollar, FaPrint } from "react-icons/fa";
import { GrUser, GrUserFemale } from "react-icons/gr";
import Modal from "../../components/Modal";
import Receipt from "./receipt";
import { formatToZMW } from "../../helpers";

const Dash = ({ allData, user, receipts }) => {
  const [showAllReceipts, setShowAllReceipts] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);

  const totalIncome = receipts.reduce(
    (sum, receipt) =>
      sum + receipt.transactions.reduce((tSum, t) => tSum + parseFloat(t.amount), 0),
    0
  );

  const handlePrintLastReceipt = () => {
    if (receipts.length > 0) {
      setSelectedReceipt(receipts[0]);
      setShowReceiptModal(true);
    }
  };

  const handlePrintReceipt = (receipt) => {
    setSelectedReceipt(receipt);
    setShowReceiptModal(true);
  };

  return (
    <div className="p-4 space-y-8 min-h-screen">
      {/* Hero Card */}
      <div className="bg-blue-600 rounded-3xl p-6 text-white shadow-xl relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/10 rounded-full" />
        <div className="flex items-center justify-between relative z-10">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
              {user?.sex?.toLowerCase() === "male" ? (
                <GrUser className="text-white text-2xl" />
              ) : (
                <GrUserFemale className="text-white text-2xl" />
              )}
            </div>
            <div>
              <h2 className="text-xl font-bold">Welcome {user?.firstname || "User"}</h2>
              <p className="text-indigo-200 text-sm">{new Date().toLocaleDateString()}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-indigo-200">Total Revenue</p>
            <h3 className="text-2xl font-extrabold">K{formatToZMW(totalIncome)}</h3>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-4">
        <button
          onClick={() => setShowAllReceipts(true)}
          className="bg-white rounded-2xl p-5 flex flex-col items-center justify-center shadow-lg border border-indigo-100 hover:scale-[1.02] active:scale-[0.98] transition-transform"
        >
          <FaFileInvoiceDollar className="text-indigo-600 text-3xl mb-2" />
          <span className="text-sm font-semibold">Receipts</span>
          <span className="text-xs text-gray-500">{receipts.length}</span>
        </button>

        <button
          onClick={handlePrintLastReceipt}
          disabled={receipts.length === 0}
          className={`rounded-2xl p-5 flex flex-col items-center justify-center shadow-lg transition-transform border ${
            receipts.length === 0
              ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
              : "bg-white border-indigo-100 hover:scale-[1.02] active:scale-[0.98]"
          }`}
        >
          <FaPrint className={`text-3xl mb-2 ${receipts.length === 0 ? "text-gray-400" : "text-indigo-600"}`} />
          <span className="text-sm font-semibold">Last Receipt</span>
          <span className="text-xs text-gray-500">Quick Print</span>
        </button>
      </div>

      {/* Recent Receipts */}
      <div>
        <h3 className="text-sm font-bold text-gray-700 mb-3">Recent Receipts</h3>
        <div className="space-y-3">
          {receipts.length > 0 ? (
            receipts
              .slice(0, 5)
              .sort((a, b) => new Date(b.date) - new Date(a.date))
              .map((receipt) => (
                <div
                  key={receipt.voucher}
                  onClick={() => handlePrintReceipt(receipt)}
                  className="p-4 rounded-2xl bg-white shadow-md border border-gray-100 flex justify-between items-center cursor-pointer hover:shadow-lg hover:bg-indigo-50/30 transition"
                >
                  <div>
                    <p className="text-sm font-semibold text-gray-800">#{receipt.voucher}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(receipt.date).toLocaleDateString()} • {receipt.paye}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-green-600">
                      K{receipt.transactions
                        .reduce((sum, t) => sum + parseFloat(t.amount), 0)
                        .toFixed(2)}
                    </p>
                    <p className="text-xs text-gray-400">
                      {receipt.transactions.length} items
                    </p>
                  </div>
                </div>
              ))
          ) : (
            <div className="p-8 text-center text-gray-500 bg-white rounded-2xl shadow-inner">
              No receipts yet
            </div>
          )}
        </div>
      </div>

      {/* All Receipts Modal */}
      {showAllReceipts && (
        <Modal title="All Receipts" onClose={() => setShowAllReceipts(false)}>
          <div className="max-h-96 overflow-y-auto space-y-2">
            {receipts.map((receipt) => (
              <div
                key={receipt.voucher}
                onClick={() => handlePrintReceipt(receipt)}
                className="p-3 rounded-xl bg-white shadow-sm border border-gray-100 flex justify-between items-center cursor-pointer hover:bg-indigo-50/50 transition"
              >
                <div>
                  <p className="text-sm font-medium text-gray-800">#{receipt.voucher}</p>
                  <p className="text-xs text-gray-500">
                    {new Date(receipt.date).toLocaleDateString()} • {receipt.paye}
                  </p>
                </div>
                <p className="text-sm font-bold text-green-600">
                  K{receipt.transactions
                    .reduce((sum, t) => sum + parseFloat(t.amount), 0)
                    .toFixed(2)}
                </p>
              </div>
            ))}
          </div>
        </Modal>
      )}

      {/* Receipt Print Modal */}
      {showReceiptModal && selectedReceipt && (
        <Modal title="Receipt" onClose={() => setShowReceiptModal(false)}>
          <Receipt
            receipt={selectedReceipt}
            council={allData.council[0]?.name}
          />
        </Modal>
      )}
    </div>
  );
};

export default Dash;
